import React from 'react';
import { Breadcrumb, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { HomeOutlined, BankOutlined, TeamOutlined, SettingOutlined, UserOutlined, ArrowLeftOutlined } from '@ant-design/icons';

// Map of page types to icons
const PAGE_ICONS = {
  home: HomeOutlined,
  dashboard: HomeOutlined,
  'business-units': BankOutlined,
  'business-unit': BankOutlined,
  team: TeamOutlined,
  participants: UserOutlined,
  settings: SettingOutlined,
  groups: TeamOutlined,
};

// Map breadcrumb names to their routes
const BREADCRUMB_ROUTES = {
  'Dashboard': '/dashboard',
  'Business Units': '/dashboard/business-units',
  'Organisations': '/dashboard/business-units',
  'Team': '/dashboard/team',
  'Groups': '/dashboard/team-groups',
  'Settings': '/dashboard/settings',
  'Business Unit Settings': '/dashboard/settings/business-units',
  'Campaigns': '/dashboard/campaigns',
  'Questionnaires': '/dashboard/questionnaires',
  'Quizzes': '/dashboard/questionnaires',
  'Create New': null, // Current page, not clickable
  'Edit': null, // Current page, not clickable
  'View': null, // Current page, not clickable
  'Audit Trail': '/dashboard/audit-trail',
  'Edit Member': null, // Current page, not clickable
};

// Convert string breadcrumbs to Ant Design breadcrumb items
export const createBreadcrumbItems = (breadcrumbs, clickHandlers = [], navigate = null) => {
  // Handle undefined, null, or non-array breadcrumbs
  if (!breadcrumbs || !Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
    return getDefaultBreadcrumbs(navigate);
  }

  // If already in correct format (objects with title/href), return as is
  if (typeof breadcrumbs[0] === 'object' && breadcrumbs[0].title) {
    return breadcrumbs;
  }

  // Convert string array to breadcrumb items
  const items = breadcrumbs.map((crumb, index) => {
    // Skip undefined or null values
    if (!crumb) {
      return null;
    }
    
    const isLast = index === breadcrumbs.length - 1;
    const key = crumb.toLowerCase().replace(/\s+/g, '-');
    const Icon = PAGE_ICONS[key] || null;
    const route = BREADCRUMB_ROUTES[crumb];
    
    // Determine click handler: custom handler, navigation route, or none
    let clickHandler = null;
    if (!isLast) {
      if (clickHandlers[index]) {
        clickHandler = clickHandlers[index];
      } else if (route && navigate) {
        clickHandler = () => navigate(route);
      }
    }
    
    return {
      key: key,
      title: (
        <span style={{ 
          color: isLast ? '#2E86AB' : '#666',
          fontWeight: isLast ? 600 : 400,
          cursor: clickHandler ? 'pointer' : 'default',
          textDecoration: clickHandler ? 'none' : 'none'
        }}
        onClick={clickHandler}
        onMouseEnter={(e) => {
          if (clickHandler) {
            e.target.style.textDecoration = 'underline';
          }
        }}
        onMouseLeave={(e) => {
          if (clickHandler) {
            e.target.style.textDecoration = 'none';
          }
        }}
        >
          {Icon && index === 0 && <Icon style={{ marginRight: 8 }} />}
          {crumb}
        </span>
      )
    };
  });

  return items.filter(item => item !== null);
};

// Get default breadcrumbs for pages without specific breadcrumbs
export const getDefaultBreadcrumbs = (navigate = null) => {
  const clickHandler = navigate ? () => navigate('/dashboard') : null;
  
  return [
    {
      key: 'dashboard',
      title: (
        <span style={{ 
          color: '#666',
          cursor: clickHandler ? 'pointer' : 'default'
        }}
        onClick={clickHandler}
        onMouseEnter={(e) => {
          if (clickHandler) {
            e.target.style.textDecoration = 'underline';
          }
        }}
        onMouseLeave={(e) => {
          if (clickHandler) {
            e.target.style.textDecoration = 'none';
          }
        }}
        >
          <HomeOutlined style={{ marginRight: 8 }} />
          Dashboard
        </span>
      ),
    }
  ];
};

// Common breadcrumb patterns
export const BREADCRUMB_PATTERNS = {
  dashboard: ['Dashboard'],
  businessUnits: ['Dashboard', 'Business Units'],
  businessUnitDetail: (businessUnitName) => ['Dashboard', 'Business Units', businessUnitName],
  businessUnitEdit: ['Dashboard', 'Business Units', 'Edit Business Unit'],
  businessUnitCreate: ['Dashboard', 'Business Units', 'Create Business Unit'],
  teamManagement: (businessUnitName) => ['Dashboard', 'Business Units', businessUnitName, 'Team Management'],
  team: ['Dashboard', 'Team'],
  teamEdit: ['Dashboard', 'Team', 'Edit Member'],
  groups: ['Dashboard', 'Team', 'Groups'],
  memberDetails: (memberName) => ['Dashboard', 'Participants', memberName || 'Member Details'],
  businessUnitMemberDetails: (businessUnitName, memberName) => ['Dashboard', 'Business Units', businessUnitName, 'Team Management', memberName || 'Member Details'],
  settings: ['Dashboard', 'Settings'],
  businessUnitSettings: ['Dashboard', 'Settings', 'Business Unit Settings'],
  campaigns: ['Dashboard', 'Campaigns'],
  newCampaign: ['Dashboard', 'Campaigns', 'New Campaign'],
  editCampaign: (campaignName) => ['Dashboard', 'Campaigns', campaignName || 'Edit Campaign'],
  campaignReport: (campaignName) => ['Dashboard', 'Campaigns', campaignName || 'Campaign Report'],
  questionnaires: ['Dashboard', 'Quizzes'],
  questionnaireCreate: ['Dashboard', 'Quizzes', 'Create New'],
  questionnaireEdit: (questionnaireName) => ['Dashboard', 'Quizzes', questionnaireName || 'Edit'],
  questionnaireView: (questionnaireName) => ['Dashboard', 'Quizzes', questionnaireName || 'View'],
  questionnaireTake: (questionnaireName) => ['Dashboard', 'Quizzes', questionnaireName || 'Take Quiz'],
  auditTrail: ['Dashboard', 'Audit Trail'],
};

// Breadcrumb component with common functionality
const BreadcrumbNav = ({ breadcrumbs, clickHandlers, style, showBackButton = true }) => {
  const navigate = useNavigate();
  const items = createBreadcrumbItems(breadcrumbs, clickHandlers, navigate);

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px',
      marginBottom: '16px',
      ...style 
    }}>
      {showBackButton && (
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{
            color: '#000',
            border: 'none',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
            height: 'auto'
          }}
          title="Go back"
        />
      )}
      <Breadcrumb 
        items={items}
        style={{ flex: 1 }}
      />
    </div>
  );
};

export default BreadcrumbNav;